import { useState } from 'react';
import SEOHelmet from '@/components/feature/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍' },
    { key: 'reputation' as const, label: '品牌口碑' },
    { key: 'contact' as const, label: '联系我们' },
  ];

  const seoData = {
    title: '关于我们 - 777影院 | 品牌介绍与联系方式',
    description: '777影院官网致力于为用户提供7777影院、777777影院、77777影院等数字变体统一入口，帮助用户快速进入官方首页免费观看高清电影和电视剧全集。了解我们的品牌故事与口碑。',
    keywords: '777影院,关于777影院,777影院品牌,777影院联系方式,影视导航入口',
    canonicalUrl: 'https://777cinema.com/about',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于777影院',
      description: '777影院官网品牌介绍、口碑与联系方式',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '777影院',
    url: 'https://777cinema.com/',
    description: '777影院官网提供高清电影和电视剧免费在线观看导航服务',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '777影院是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '777影院是一个影视导航入口网站，提供7777影院、777777影院、77777影院、7777777影院等多个数字变体的统一入口，帮助用户快速找到官方首页，免费观看高清电影和电视剧全集。',
        },
      },
      {
        '@type': 'Question',
        name: '777影院和7777影院是什么关系？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '7777影院是777影院的一个数字变体入口。由于域名和访问方式的多样性，777影院统一提供7777影院、777777影院、77777影院、7777777影院等所有变体的导航服务，确保用户始终能够访问到正确的官方首页。',
        },
      },
      {
        '@type': 'Question',
        name: '777影院需要注册会员吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '777影院作为影视导航入口，本身不需要注册。用户可以通过我们提供的导航链接直接进入目标影院网站进行免费观看。具体观看网站的会员政策请以目标网站为准。',
        },
      },
      {
        '@type': 'Question',
        name: '777影院上的电影资源是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '777影院导航指向的影院网站通常提供免费的高清电影和电视剧在线观看服务。我们致力于为用户筛选稳定可靠的免费影视资源入口。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在手机上看777影院的电影？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '777影院的导航入口支持手机浏览器直接访问，无需下载任何APP。只需在手机浏览器中打开777影院网站，即可通过导航链接进入目标影院观看高清电影和电视剧。',
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://777cinema.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SEOHelmet data={seoData} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]} />
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mt-4">关于我们</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-8 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'intro' && (
            <section>
              <div className="relative w-full aspect-[21/9] mb-8 overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20cinema%20lobby%20interior%20with%20warm%20golden%20lighting%20elegant%20movie%20poster%20displays%20and%20comfortable%20waiting%20area%2C%20professional%20architectural%20photography%2C%20clean%20composition%2C%20cinematic%20atmosphere%2C%20luxurious%20yet%20welcoming%20design&width=1200&height=500&seq=about-intro-777&orientation=landscape"
                  alt="777影院品牌介绍"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed">
                <h2>关于777影院</h2>
                <p>
                  777影院是一个专业的影视导航门户网站，致力于为广大影视爱好者提供便捷、可靠的观影入口服务。我们深知在互联网上找到稳定可用的影视网站并非易事，因此777影院汇集了<strong>7777影院、777777影院、77777影院、7777777影院</strong>等多个数字变体入口，确保用户始终能够快速进入官方首页。
                </p>

                <h2>我们的使命</h2>
                <p>
                  在信息繁杂的互联网时代，影视网站的域名和入口经常发生变化。777影院的使命就是成为用户最信赖的影视导航平台——无论域名如何变化，用户只需记住777影院，就能找到所有可用的官方入口。我们持续监控和更新导航链接，确保每一个入口的稳定性和可用性。
                </p>

                <h2>核心优势</h2>
                <ul>
                  <li><strong>多入口保障：</strong>同时提供7777影院、777777影院、77777影院、7777777影院等多个变体入口，一个失效还有其他可用</li>
                  <li><strong>实时更新：</strong>7×24小时监控链接可用性，第一时间更新失效入口</li>
                  <li><strong>免费使用：</strong>导航服务完全免费，无需注册、无需下载任何软件</li>
                  <li><strong>全平台支持：</strong>电脑、手机、平板均可访问，随时随地开始观影</li>
                  <li><strong>内容丰富：</strong>涵盖动作片、喜剧片、科幻片、动漫、纪录片等所有主流类型</li>
                </ul>

                <h2>数字变体说明</h2>
                <p>
                  您可能在搜索中看到过<strong>7777影院</strong>、<strong>777777影院</strong>、<strong>77777影院</strong>、<strong>7777777影院</strong>等不同名称，它们都是777影院的数字变体入口。由于网络环境的复杂性，不同变体入口可能指向不同的服务器，但它们提供的服务和内容本质上是相同的。777影院官网将这些入口统一整合，方便用户一键切换。
                </p>
              </div>
            </section>
          )}

          {activeTab === 'reputation' && (
            <section>
              <h2 className="text-lg font-bold text-foreground-950 mb-6">品牌口碑</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { title: '稳定可靠', desc: '99.9%的入口可用率，多重备份确保用户随时能够访问', icon: 'ri-shield-check-line' },
                  { title: '更新及时', desc: '链接失效后最快5分钟内完成更新替换，不影响用户观影', icon: 'ri-refresh-line' },
                  { title: '资源丰富', desc: '覆盖10000+部高清电影和电视剧，类型齐全满足各种口味', icon: 'ri-stack-line' },
                  { title: '用户信赖', desc: '日均服务超过50万影视爱好者，已成为最受欢迎的影视导航平台之一', icon: 'ri-heart-line' },
                ].map((item, i) => (
                  <div key={i} className="bg-background-100 rounded-lg p-5 border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">{item.title}</h3>
                    <p className="text-xs text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">用户评价</h3>
                <div className="flex flex-col gap-4">
                  {[
                    { name: '张***', text: '一直用777影院找入口，非常方便！之前经常找不到7777影院的地址，有了这个导航再也不用担心了。', rating: 5 },
                    { name: '李***', text: '资源很全，动作片、科幻片都有，更新也快。推荐给好几个朋友了，都说好用。', rating: 5 },
                    { name: '王***', text: '手机上也能直接打开，不用下载APP，非常方便。希望一直保持这样的服务质量！', rating: 4 },
                    { name: '陈***', text: '作为影视迷，收藏了777影院已经半年多了。入口稳定，分类清晰，找片很方便。', rating: 5 },
                  ].map((review, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-accent-600">{review.name.charAt(0)}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-foreground-950">{review.name}</span>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: review.rating }).map((_, j) => (
                              <i key={j} className="ri-star-fill text-[10px] text-amber-400"></i>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-foreground-600">{review.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'contact' && (
            <section>
              <h2 className="text-lg font-bold text-foreground-950 mb-6">联系我们</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-background-100 rounded-lg p-6 border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-information-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">重要提示</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    777影院是一个影视导航入口网站，我们<strong>不存储任何视频内容</strong>，也不提供视频播放服务。本网站仅提供相关影视网站的导航链接，帮助用户快速找到所需的观影入口。
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg p-6 border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-links-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">链接反馈</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    如果您发现某个导航链接无法访问，或知道新的可用入口，欢迎通过以下方式告诉我们。我们会第一时间核实并更新。
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">数字变体入口汇总</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {['7777影院', '777777影院', '77777影院', '7777777影院'].map((v) => (
                    <div key={v} className="text-center p-3 bg-background-50 rounded-lg border border-background-200">
                      <span className="text-sm font-medium text-foreground-950">{v}</span>
                      <p className="text-xs text-foreground-500 mt-1">数字变体入口</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-lg font-bold text-foreground-950 mb-6 text-center">常见问题</h2>
            <div className="flex flex-col gap-3">
              {[
                { q: '777影院是什么？', a: '777影院是一个影视导航入口网站，提供7777影院、777777影院、77777影院、7777777影院等多个数字变体的统一入口，帮助用户快速找到官方首页，免费观看高清电影和电视剧全集。' },
                { q: '777影院和7777影院是什么关系？', a: '7777影院是777影院的一个数字变体入口。由于域名和访问方式的多样性，777影院统一提供所有变体的导航服务，确保用户始终能够访问到正确的官方首页。' },
                { q: '777影院需要注册会员吗？', a: '777影院作为影视导航入口，本身不需要注册。用户可以通过我们提供的导航链接直接进入目标影院网站进行免费观看。具体观看网站的会员政策请以目标网站为准。' },
                { q: '777影院上的电影资源是免费的吗？', a: '777影院导航指向的影院网站通常提供免费的高清电影和电视剧在线观看服务。我们致力于为用户筛选稳定可靠的免费影视资源入口。' },
                { q: '如何在手机上看777影院的电影？', a: '777影院的导航入口支持手机浏览器直接访问，无需下载任何APP。只需在手机浏览器中打开777影院网站，即可通过导航链接进入目标影院观看高清电影和电视剧。' },
              ].map((faq, i) => (
                <details key={i} className="bg-background-50 rounded-lg border border-background-200 group">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-950 list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}